import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"

import {
    useUpdateableState,
    startOfDay,
    daysInMonth,
    getTimeZoneNamed,
    firstMondayOfWeek
} from "@lib"

const DatePicker: React.FunctionComponent<{
    className?: string
    title?: string,
    placeholder?: string,
    min?: Date,
    max?: Date,
    value?: Date | null,
    helperText?: string,
    required?: boolean,
    disabled?: boolean,
    error?: Error,
    showTimePicker?: boolean
    timezone?: string,
    onChange?: (date: Date | null) => void
}> = ({
    className,
    title,
    placeholder,
    min,
    max,
    value,
    helperText,
    required,
    disabled,
    error,
    showTimePicker,
    timezone = new Intl.DateTimeFormat("en").resolvedOptions().timeZone,
    onChange
}) => {
    const today = useMemo(() => {
        const today = new Date()
        // Set time components to beginning of day
        today.setHours(0, 0, 0, 0)
        return today
    }, [])

    const [internalValue, setInternalValue] = useUpdateableState<Date>(
        value || undefined,
        (current, next) => current?.getTime() !== next?.getTime()
    )

    const [selectedYear, setSelectedYear] = useUpdateableState((internalValue || today)?.getFullYear())
    const [selectedMonth, setSelectedMonth] = useUpdateableState((internalValue || today).getMonth())
    const [selectedDate, setSelectedDate] = useUpdateableState((internalValue || today)?.getDate())

    const [selectedHours, setSelectedHours] = useUpdateableState((internalValue || today).getHours())
    const [selectedMinutes, setSelectedMinutes] = useUpdateableState((internalValue || today).getMinutes())
    const [isAmOrPm, setAmPm] = useUpdateableState<"AM" | "PM">((internalValue || today).getHours() >= 12 ? "PM" : "AM")

    const tzOffsetMinutes = useMemo(() => {
        return getTimeZoneNamed(timezone)?.offsetMinutes || 0
    }, [timezone])

    const numberOfDaysInSelectedMonth = useMemo(() => {
        return daysInMonth(selectedYear!, selectedMonth!)
    }, [selectedYear, selectedMonth])

    const firstDayOfSelectedMonth = useMemo(() => {
        const date = new Date(selectedYear!, selectedMonth!, 1)
        date.setHours(0, 0, 0, 0)
        return date
    }, [selectedYear, selectedMonth])

    const startOfFirstMondayInSelectedMonth = firstMondayOfWeek(firstDayOfSelectedMonth)
    const offsetDays = useMemo(() => {
        const offset = (firstDayOfSelectedMonth.getTime() - startOfFirstMondayInSelectedMonth.getTime()) / (86400 * 1000)
        return offset > 0 ? offset : 7 - Math.abs(offset)
    }, [firstDayOfSelectedMonth, startOfFirstMondayInSelectedMonth])

    // Number of years to next nearest decade.
    // We use this value to cap (maximum) year input.
    const yearsToNextDecade = (today.getFullYear() % 10 === 0)
        ? 10
        : (10 - (today.getFullYear() % 10))

    const [isDatePickerVisible, setDatePickerVisible] = useState<boolean>(false)

    const decrementMonthHandler = useCallback(() => {
        const nextSelectedMonth = selectedMonth! - 1
        setSelectedMonth(nextSelectedMonth < 0 ? 11 : nextSelectedMonth)
        if (nextSelectedMonth < 0) {
            setSelectedYear(Math.max(1970, selectedYear! - 1))
        }
    }, [selectedMonth, selectedYear, setSelectedMonth, setSelectedYear])

    const incrementMonthHandler = useCallback(() => {
        const nextSelectedMonth = (selectedMonth! + 1) % 12
        setSelectedMonth(nextSelectedMonth)
        if (selectedMonth === 11) {
            setSelectedYear(selectedYear! + 1)
        }
    }, [selectedMonth, selectedYear, setSelectedMonth, setSelectedYear])

    // Ref to keep track of whether this is the first render (onMount())
    const firstRender = useRef(true)
    // Ref to keep track of the last parameter to onChange call
    const timestampRef = useRef<number>()

    // Memoized function to calculate timestamp value given the
    // selected year, month, date, hour, minutes, and timezone
    const timestamp = useMemo(() => {
        if (!internalValue) {
            return undefined
        }

        const date = new Date()
        date.setFullYear(selectedYear!)
        date.setMonth(selectedMonth!)
        date.setDate(selectedDate!)
        date.setHours((selectedHours!), selectedMinutes, 0, 0)
        // date.setTime(date.getTime() + (-1 * (tzOffsetMinutes || 0) * 60 * 1000))
        return date.getTime()
    }, [
        internalValue,
        selectedDate,
        selectedYear,
        selectedMonth,
        selectedHours,
        selectedMinutes,
        // tzOffsetMinutes
    ])

    useEffect(() => {
        // First render shouldn't cause onChange to be called
        if (firstRender.current) {
            firstRender.current = false
            return
        }

        // Use React's way (Object.is()) of comparing if object is similar.
        // We compare between the previous parameter passed to onChange
        // and the new parameter, to only call onChange if they are
        // different.
        if (!Object.is(timestamp, timestampRef.current)) {
            onChange && onChange(timestamp ? new Date(timestamp) : null)
            timestampRef.current = timestamp
        }
    }, [timestamp, onChange])

    return (
        <div tabIndex={-1}
            className={className}
            onClick={(event) => {
                if (disabled) {
                    return
                }
                !isDatePickerVisible && setDatePickerVisible(true)
            }}
            onBlur={(event) => {
                if (event.currentTarget.contains(event.relatedTarget)) {
                    return
                }
                if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
                    isDatePickerVisible && setDatePickerVisible(false)
                }
            }}>
            {title &&
                <label className="block text-footnote text-black-700 text-left mb-[8px]">
                    <span>{title}</span>
                    {required && <span className="text-alert-500 ml-[4px]">*</span>}
                </label>
            }
            <div tabIndex={-1}
                className={["flex w-full mt-[4px] py-[12px] px-[16px] bg-white-900 border border-black-500 focus:outline-primary-900 rounded text-body text-black-900"].join(" ")}>
                <div className="flex-auto">
                    {showTimePicker
                        ? (<>{timestamp ? <span>{new Date(timestamp).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}, {new Date(timestamp).toLocaleTimeString("en-US", { hour: "2-digit", hour12: true, minute: "2-digit", timeZoneName: "short" })}</span> : <span className="text-gray-500">{placeholder}</span> || ""}</>)
                        : <>{timestamp ? <span>{new Date(timestamp).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}</span> : <span className="text-gray-500">{placeholder}</span> || ""}</>
                    }
                </div>

                {timestamp && !disabled &&
                    <button type="button" onClick={() => {
                        setInternalValue(undefined)
                        setDatePickerVisible(false)
                    }}>
                        <i className="text-black-400 fa-solid fa-xmark"></i>
                    </button>
                }

                {!disabled && <button type="button" className={["ml-[8px] w-[30px] transform duration-300"].concat(isDatePickerVisible ? ["rotate-180"] : ["rotate-0"]).join(" ")}
                    onClick={() => isDatePickerVisible && setDatePickerVisible(false)}>
                    <Image width={24} height={24} objectFit="contain" src="/images/ic_chevron_down_accent.svg" alt="Dropdown Icon" />
                </button>}
            </div>

            {helperText && !error && <span className="text-gray-400 text-caption3">{helperText}</span>}
            {error && <div className="text-caption2 text-alert-900"><b>{error.message}</b></div>}

            {isDatePickerVisible && (
            <div className="relative">
                <div className="absolute z-20 right-0 inline-flex bg-white-900 rounded-[8px] shadow-md px-[16px] py-[12px]">
                    <div>
                        <div className="flex gap-x-[4px] text-body">
                            <button type="button" className="w-[30px] hover:bg-accent-100"
                                onClick={decrementMonthHandler}>
                                <i className="fa-solid fa-chevron-left text-accent-900"></i>
                            </button>

                            <select
                                className="text-body"
                                name="month"
                                value={selectedMonth}
                                onChange={(event) => setSelectedMonth(parseInt(event.target.value))}
                                required>
                                <option value="0">January</option>
                                <option value="1">February</option>
                                <option value="2">March</option>
                                <option value="3">April</option>
                                <option value="4">May</option>
                                <option value="5">June</option>
                                <option value="6">July</option>
                                <option value="7">August</option>
                                <option value="8">September</option>
                                <option value="9">October</option>
                                <option value="10">November</option>
                                <option value="11">December</option>
                            </select>

                            <input
                                className="text-body"
                                type="number"
                                min={"1900"}
                                max={`${today.getFullYear() + yearsToNextDecade}`}
                                value={`${selectedYear}`}
                                onChange={(event) => {
                                    if (isNaN(parseInt(event.target.value))) {
                                        return
                                    }

                                    setSelectedYear(parseInt(event.target.value))
                                }}
                                required
                                />

                            <button type="button" className="w-[40px]"
                                onClick={incrementMonthHandler}>
                                <i className="fa-solid fa-chevron-right text-accent-900"></i>
                            </button>
                        </div>

                        <div className="mt-[20px]">
                            <div id="weekdayHeader" className="grid grid-cols-7 w-[280px]">
                            {[0, 1, 2, 3, 4, 5, 6].map((each) => {
                                const referenceDate = new Date(startOfFirstMondayInSelectedMonth.getTime() + (each * 86400 * 1000))
                                return (
                                    <div key={each} className="w-[40px] col-span-1 text-caption2 text-center text-gray-500 uppercase">
                                        <b>{referenceDate.toLocaleDateString("en-US", { weekday: "short" })}</b>
                                    </div>
                                )
                            })}
                            </div>

                            <div id="monthView" className="grid grid-cols-7 mt-[8px] w-[280px]">
                            {Array(numberOfDaysInSelectedMonth + offsetDays).fill(0, 0).map((each, index) => {
                                const date = new Date(startOfFirstMondayInSelectedMonth.getTime() + (index * 86400 * 1000))

                                if (date.getMonth() !== selectedMonth || date.getFullYear() !== selectedYear) {
                                    // Return empty div else empty element won't be rendered on the DOM
                                    return <div></div>
                                }

                                const isToday = date.getTime() === today.getTime()
                                const isSelectedDate = timestamp && date.getTime() === startOfDay(new Date(timestamp)).getTime()

                                const isValid = (min === undefined || min === null ||
                                    (
                                        isToday
                                            ? date.getFullYear() === min.getFullYear() && date.getMonth() === min.getMonth() && date.getDate() === min.getDate()
                                            : date.getTime() >= min.getTime()
                                    )
                                ) && (max === undefined || max === null ||
                                    (
                                        isToday
                                            ? date.getFullYear() === max.getFullYear() && date.getMonth() === max.getMonth() && date.getDate() === max.getDate()
                                            : date.getTime() <= max.getTime()
                                    )
                                )

                                if (isToday) {
                                    return (
                                        <button type="button" key={index}
                                            className={
                                                ["w-[40px] h-[40px] text-body text-center hover:rounded "]
                                                    .concat(isSelectedDate ? ["bg-primary-200 rounded"] : [])
                                                    .concat(isValid ? ["text-primary-700 hover:rounded hover:bg-accent-100 cursor-pointer"] : ["text-gray-500 cursor-default"])
                                                    .join(" ")
                                            }
                                            onClick={() => {
                                                if (!isValid) return
                                                setInternalValue(date)
                                                setSelectedDate(date.getDate())
                                            }}>
                                            <b>{date.getDate()}</b>
                                        </button>
                                    )
                                } else {
                                    return (
                                        <button type="button" key={index}
                                            className={
                                                ["w-[40px] h-[40px] text-body text-center"]
                                                    .concat(isSelectedDate ? ["bg-primary-200 text-white rounded"] : [])
                                                    .concat(isValid ? ["text-black hover:rounded hover:bg-accent-100 cursor-pointer"] : ["text-gray-500 cursor-default"])
                                                    .join(" ")
                                            }
                                            onClick={() => {
                                                if (!isValid) return
                                                setInternalValue(date)
                                                setSelectedDate(date.getDate())
                                            }}>
                                            {date.getDate()}
                                        </button>
                                    )
                                }
                            })}
                            </div>
                        </div>

                        {showTimePicker && internalValue && (
                        <div className="mt-[12px]">
                            <input
                                className="p-[4px] text-body bg-black-100"
                                type="number"
                                min="0"
                                max="12"
                                value={String((selectedHours || 0) % 12).padStart(2, "0")}
                                onChange={(event) => {
                                    const hours = parseInt(event.target.value)
                                    setSelectedHours(isAmOrPm === "PM" ? hours + 12 : hours)
                                }}
                                />

                            <span>:</span>

                            <input
                                className="p-[4px] text-body bg-black-100"
                                type="number"
                                min="0"
                                max="59"
                                value={String(selectedMinutes).padStart(2, "0")}
                                onChange={(event) => {
                                    const minutes = parseInt(event.target.value)
                                    setSelectedMinutes(minutes)
                                }}
                                />

                            <select
                                className="p-[4px] text-body"
                                name="amOrPm"
                                value={isAmOrPm}
                                onChange={(event) => {
                                    if (isAmOrPm === "AM" && event.target.value === "PM") {
                                        setSelectedHours((selectedHours || 0) + 12)
                                    } else if (isAmOrPm === "PM" && event.target.value === "AM") {
                                        setSelectedHours((selectedHours || 0) - 12)
                                    }
                                    setAmPm(event.target.value === "AM" ? "AM" : "PM")}
                                }>
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                            </select>
                        </div>
                        )}
                    </div>
                </div>
            </div>
            )}
        </div>
    )
}

export default DatePicker
